import React, { useEffect, useState } from 'react';
import { patientHistoryService, type GynecolologicalHistory } from '../services/patientHistoryService';
import Spinner from '../../../components/ui/Spinner';

interface GynecolologicalHistoryFormProps {
  patientId: number;
}

interface FormData {
  menarche_age: number | '';
  menstrual_pattern: 'regular' | 'irregular' | '';
  last_menstruation_date: string;
  is_menopausal: boolean;
  menopausal_age: number | '';
  pap_date: string;
  pap_result: string;
  mammogram_date: string;
  mammogram_result: string;
  contraceptive_method: string;
  is_sexually_active: boolean;
  sexual_activity_notes: string;
}

const emptyForm: FormData = {
  menarche_age: '',
  menstrual_pattern: '',
  last_menstruation_date: '',
  is_menopausal: false,
  menopausal_age: '',
  pap_date: '',
  pap_result: '',
  mammogram_date: '',
  mammogram_result: '',
  contraceptive_method: '',
  is_sexually_active: false,
  sexual_activity_notes: '',
};

const GynecolologicalHistoryForm: React.FC<GynecolologicalHistoryFormProps> = ({ patientId }) => {
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hasData, setHasData] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await patientHistoryService.getGynecologicalHistory(patientId);
        if (data) {
          setHasData(true);
          setFormData({
            menarche_age: data.menarche_age ?? '',
            menstrual_pattern: data.menstrual_pattern ?? '',
            last_menstruation_date: data.last_menstruation_date ?? '',
            is_menopausal: data.is_menopausal ?? false,
            menopausal_age: data.menopausal_age ?? '',
            pap_date: data.pap_date ?? '',
            pap_result: data.pap_result ?? '',
            mammogram_date: data.mammogram_date ?? '',
            mammogram_result: data.mammogram_result ?? '',
            contraceptive_method: data.contraceptive_method ?? '',
            is_sexually_active: data.is_sexually_active ?? false,
            sexual_activity_notes: data.sexual_activity_notes ?? '',
          });
        } else {
          setHasData(false);
        }
      } catch (err: any) {
        setError(err.response?.data?.message || 'Error al cargar historial ginecológico');
      } finally {
        setLoading(false);
      }
    };

    void fetchData();
  }, [patientId]);

  const handleChange = (field: keyof FormData, value: string | boolean | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const payload: GynecolologicalHistory = {
        patient_id: patientId,
        menarche_age: formData.menarche_age === '' ? null : Number(formData.menarche_age),
        menstrual_pattern: formData.menstrual_pattern === '' ? null : formData.menstrual_pattern,
        last_menstruation_date: formData.last_menstruation_date || null,
        is_menopausal: formData.is_menopausal,
        menopausal_age: formData.menopausal_age === '' ? null : Number(formData.menopausal_age),
        pap_date: formData.pap_date || null,
        pap_result: formData.pap_result || null,
        mammogram_date: formData.mammogram_date || null,
        mammogram_result: formData.mammogram_result || null,
        contraceptive_method: formData.contraceptive_method || null,
        is_sexually_active: formData.is_sexually_active,
        sexual_activity_notes: formData.sexual_activity_notes || null,
      };

      await patientHistoryService.updateGynecologicalHistory(patientId, payload);
      setSuccess('Historial ginecológico guardado exitosamente');
      setHasData(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al guardar historial ginecológico');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Spinner variant="full-page" text="Cargando historial ginecológico..." />;
  }

  if (!hasData && !error) {
    return (
      <div className="alert alert-info">
        <i className="fa fa-info-circle me-2"></i>
        No hay datos de historial ginecológico registrados.
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">
          <i className="fa fa-venus"></i> Historia Ginecológica
        </h3>
      </div>
      <div className="card-body">
        {error && (
          <div className="alert alert-danger alert-sm mb-3">
            <i className="fa fa-exclamation-circle me-2"></i>
            {error}
          </div>
        )}
        {success && (
          <div className="alert alert-success alert-sm mb-3">
            <i className="fa fa-check-circle me-2"></i>
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="row">
            {/* Menarche Age */}
            <div className="col-md-4">
              <div className="form-group">
                <label>Edad de Menarca</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.menarche_age}
                  onChange={(e) => handleChange('menarche_age', e.target.value)}
                  min={8}
                  max={20}
                  placeholder="Ej: 12"
                />
              </div>
            </div>

            {/* Menstrual Pattern */}
            <div className="col-md-4">
              <div className="form-group">
                <label>Patrón Menstrual</label>
                <select
                  className="form-control"
                  value={formData.menstrual_pattern}
                  onChange={(e) => handleChange('menstrual_pattern', e.target.value)}
                >
                  <option value="">Seleccionar...</option>
                  <option value="regular">Regular</option>
                  <option value="irregular">Irregular</option>
                </select>
              </div>
            </div>

            {/* Last Menstruation Date */}
            <div className="col-md-4">
              <div className="form-group">
                <label>Última Menstruación</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.last_menstruation_date}
                  onChange={(e) => handleChange('last_menstruation_date', e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="row">
            {/* Is Menopausal */}
            <div className="col-md-4">
              <div className="form-group">
                <label>¿Es Menopáusica?</label>
                <div>
                  <label className="me-3">
                    <input
                      type="checkbox"
                      checked={formData.is_menopausal}
                      onChange={(e) => handleChange('is_menopausal', e.target.checked)}
                    />
                    {' '}Sí
                  </label>
                </div>
              </div>
            </div>

            {/* Menopausal Age */}
            {formData.is_menopausal && (
              <div className="col-md-4">
                <div className="form-group">
                  <label>Edad de Menopausia</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.menopausal_age}
                    onChange={(e) => handleChange('menopausal_age', e.target.value)}
                    min={30}
                    max={65}
                    placeholder="Ej: 50"
                  />
                </div>
              </div>
            )}

            {/* Contraceptive Method */}
            <div className="col-md-4">
              <div className="form-group">
                <label>Método Anticonceptivo</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.contraceptive_method}
                  onChange={(e) => handleChange('contraceptive_method', e.target.value)}
                  placeholder="Ej: DIU, Pastillas, etc."
                />
              </div>
            </div>
          </div>

          <div className="row">
            {/* Is Sexually Active */}
            <div className="col-md-4">
              <div className="form-group">
                <label>¿Sexualmente Activa?</label>
                <div>
                  <label className="me-3">
                    <input
                      type="checkbox"
                      checked={formData.is_sexually_active}
                      onChange={(e) => handleChange('is_sexually_active', e.target.checked)}
                    />
                    {' '}Sí
                  </label>
                </div>
              </div>
            </div>

            {/* Sexual Activity Notes */}
            {formData.is_sexually_active && (
              <div className="col-md-8">
                <div className="form-group">
                  <label>Notas de Actividad Sexual</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={formData.sexual_activity_notes}
                    onChange={(e) => handleChange('sexual_activity_notes', e.target.value)}
                    placeholder="Notas adicionales..."
                  />
                </div>
              </div>
            )}
          </div>

          <hr />

          <h5>Estudios</h5>
          <div className="row">
            {/* PAP Date */}
            <div className="col-md-3">
              <div className="form-group">
                <label>Fecha de Papanicolaou (PAP)</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.pap_date}
                  onChange={(e) => handleChange('pap_date', e.target.value)}
                />
              </div>
            </div>

            {/* PAP Result */}
            <div className="col-md-3">
              <div className="form-group">
                <label>Resultado de PAP</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={formData.pap_result}
                  onChange={(e) => handleChange('pap_result', e.target.value)}
                  placeholder="Resultado..."
                />
              </div>
            </div>

            {/* Mammogram Date */}
            <div className="col-md-3">
              <div className="form-group">
                <label>Fecha de Mamografía</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.mammogram_date}
                  onChange={(e) => handleChange('mammogram_date', e.target.value)}
                />
              </div>
            </div>

            {/* Mammogram Result */}
            <div className="col-md-3">
              <div className="form-group">
                <label>Resultado de Mamografía</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={formData.mammogram_result}
                  onChange={(e) => handleChange('mammogram_result', e.target.value)}
                  placeholder="Resultado..."
                />
              </div>
            </div>
          </div>

          <div className="form-group mt-3">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? (
                <Spinner size="sm" text="Guardando..." />
              ) : (
                <><i className="fa fa-save"></i> Guardar Historia Ginecológica</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GynecolologicalHistoryForm;
